import { createElement } from 'react';
import { flushSync } from 'react-dom';
import { createRoot, type Root } from 'react-dom/client';
import { LiquidBody, type LiquidReaction } from '../LiquidBody';
import { CALM, PRESETS } from '../liquid';
import type { ObserverGesture, ObserverMotion, ObserverState } from '../observerContext';
import { HOME_GESTURE_MS } from '../observerEvents';
import { EASE_SETTLE } from './contact';
import { dropOnly, type Contact, type CourierBody, type CourierFinale, type Track } from './types';

/*
  Тело курьера по умолчанию (spec-3-motion §9) — наше, этап D его не ждёт; чат «жидкого помощника» меняет его
  целиком через проп провайдера body. Здесь только переходник к существующему LiquidBody, само тело не
  переделывается.

  - В пути: капля core-sphere 8 px цвета ядра (--core-tone, им правит курьер) с вытяжкой вдоль хода:
    k = 1 + S·smoothstep(0,15; 1,2; v), ширина 1/√k (площадь сохраняется, поперечник не больше 8 px). Ось
    вытяжки отстаёт от касательной на 40 мс, вытяжка нарастает за 60 мс и спадает за 140 мс; первые 80 мс —
    перешеек отрыва ×1,3.
  - Касание блока и вкладки: оседание 220 мс вдоль нормали (1,12 × 0,88 → 0,97 × 1,03 → 1), затем разворот
    капли в LiquidBody (PRESETS[state], без getEnv — к курсору не тянется) за 200 мс; центр — на кромке.
  - Касание пункта меню: «прислониться» — за 160 мс на 2 px к пункту и 0,88 по нормали × 1,12 поперёк от края,
    обращённого к пункту, за 200 мс обратно.
  - Жест: в меню — готовые [data-gesture] из motion.css на капле; на холсте — финал тела из EVENT_PLAN
    (одна волна от камня или разлив «готово»).
  - Подъём: тело сворачивается в каплю за 160 мс.
*/

/** Сила вытяжки в пути по силе деформаций (×1,2 / 1,45 / 1,8 на крейсерской). */
const STRETCH_GAIN: Record<ObserverMotion, number> = { restrained: 0.2, pronounced: 0.45, cartoon: 0.8 };
export const HEADING_LAG_MS = 40;
export const STRETCH_IN_MS = 60;
export const STRETCH_OUT_MS = 140;
/** Отрыв: первые 80 мс капля вытянута ×1,3 вдоль хода. */
const LIFTOFF_MS = 80;
const LIFTOFF_K = 1.3;
/** Шаг кадров формы, мс. */
const FRAME_MS = 16;
export const SETTLE_MS = 220;
export const UNFOLD_MS = 200;
export const FOLD_MS = 160;
/** «Прислониться» в меню: сдвиг к пункту и обратно. */
const LEAN_MS = 160;
const LEAN_BACK_MS = 200;
const LEAN_PX = 2;
/** Сколько финал тела держит касание до подъёма, мс (разлив с пульсом, одна волна). */
const FINALE_MS: Record<CourierFinale, number> = { success: 1000, wave: 900 };
/** Капля в пути и у пункта меню, px. */
const DROP = 8;

const smoothstep = (a: number, b: number, x: number) => {
  const u = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return u * u * (3 - 2 * u);
};

/** Вытяжка вдоль направления deg: вдоль k, поперёк 1/√k — без поворота самой капли (блик на месте). */
const along = (deg: number, k: number) =>
  `rotate(${deg.toFixed(2)}deg) scale(${k.toFixed(4)}, ${(1 / Math.sqrt(k)).toFixed(4)}) rotate(${(-deg).toFixed(2)}deg)`;
/** Сплющивание по нормали n (доля по нормали a, поперёк c) с точкой отсчёта в точке касания. */
const squash = (n: [number, number], a: number, c: number, shift = 0, pivot = 0) => {
  const deg = (Math.atan2(n[1], n[0]) * 180) / Math.PI;
  const px = n[0] * pivot;
  const py = n[1] * pivot;
  return (
    `translate(${(n[0] * shift + px).toFixed(2)}px, ${(n[1] * shift + py).toFixed(2)}px) ` +
    `rotate(${deg.toFixed(2)}deg) scale(${a}, ${c}) rotate(${(-deg).toFixed(2)}deg) ` +
    `translate(${(-px).toFixed(2)}px, ${(-py).toFixed(2)}px)`
  );
};

const wait = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));
const done = (a: Animation) =>
  a.finished.then(
    () => undefined,
    () => undefined,
  );

export interface DefaultBodyOptions {
  /** Состояние дома — пресет формы LiquidBody. */
  state: () => ObserverState;
}

export function createDefaultBody({ state }: DefaultBodyOptions): CourierBody {
  let tilt: HTMLElement | null = null;
  let skin: HTMLElement | null = null;
  let drop: HTMLElement | null = null;
  let host: HTMLElement | null = null;
  let root: Root | null = null;
  let k = 1;
  let shownSize = 0;
  let splashAt: number | null = null;
  let reaction: LiquidReaction | null = null;
  /** Поколение тела: снятое посреди касания или жеста не доигрывает разворот в чужой визит. */
  let gen = 0;

  /** LiquidBody у цели: рендер сразу (flushSync) — разворот начинается в этом же кадре. */
  const paint = (size: number) => {
    if (!root || !host) return;
    shownSize = size;
    const px = size * 1.8;
    host.style.left = `${-px / 2}px`;
    host.style.top = `${-px / 2}px`;
    const s = state();
    flushSync(() =>
      root!.render(
        size > 0
          ? createElement(LiquidBody, {
              params: { ...CALM, ...(PRESETS[s] ?? {}) },
              size,
              color: 'var(--core-tone)',
              colorMs: 120,
              splashAt,
              waveCount: 1,
              reaction,
            })
          : null,
      ),
    );
  };

  return {
    mount(t, s) {
      gen++;
      tilt = t;
      skin = s;
      drop = document.createElement('div');
      drop.className = 'core-sphere absolute -top-1 -left-1 size-2 rounded-full';
      const tone = document.createElement('div');
      tone.className = 'core-sphere absolute inset-0 rounded-full';
      tone.dataset.tone = '';
      tone.dataset.toneLayer = '';
      drop.appendChild(tone);
      host = document.createElement('div');
      host.className = 'absolute opacity-0';
      s.dataset.slot = 'observer';
      s.append(host, drop);
      root = createRoot(host);
    },

    travel(track: Track, motion: ObserverMotion) {
      const S = STRETCH_GAIN[motion];
      const T = track.duration;
      // Инерция формы: вытяжка догоняет цель за 60 мс и отпускает за 140 мс.
      const ks: number[] = [];
      let kk = 1;
      let last = 0;
      for (const p of track.samples) {
        const dt = Math.max(0, p.t - last);
        last = p.t;
        let goal = 1 + S * smoothstep(0.15, 1.2, p.speed);
        if (p.t < LIFTOFF_MS) goal = Math.max(goal, LIFTOFF_K);
        const tau = goal > kk ? STRETCH_IN_MS : STRETCH_OUT_MS;
        kk += (goal - kk) * (1 - Math.exp(-dt / tau));
        ks.push(kk);
      }
      const kAt = (t: number) => {
        const s = track.samples;
        let lo = 0;
        let hi = s.length - 1;
        while (hi - lo > 1) {
          const mid = (lo + hi) >> 1;
          if (s[mid].t <= t) lo = mid;
          else hi = mid;
        }
        return ks[lo];
      };
      const frames: Keyframe[] = [];
      const n = Math.max(1, Math.ceil(T / FRAME_MS));
      for (let i = 0; i <= n; i++) {
        // Доля — точно i/n: (T·i/n)/T на краю даёт 1 + ε, и WAAPI отказывает всему пути.
        const u = i / n;
        const t = T * u;
        const deg = (track.at(Math.max(0, t - HEADING_LAG_MS)).heading * 180) / Math.PI;
        // К концу пути капля круглая: стык с ядром и таблеткой.
        const end = Math.min(1, (T - t) / STRETCH_OUT_MS);
        const kt = 1 + (kAt(t) - 1) * end;
        frames.push({ offset: u, transform: along(deg, kt) });
      }
      return { tilt: frames };
    },

    async land(c: Contact) {
      if (!tilt) return;
      const my = gen;
      if (dropOnly(c.kind)) {
        // Прислониться к пункту: край капли, обращённый к пункту, — точка отсчёта.
        const n: [number, number] = [c.normal[0], c.normal[1]];
        tilt.animate(
          [
            { transform: squash(n, 1, 1) },
            {
              transform: squash(n, 0.88, 1.12, LEAN_PX, DROP / 2),
              offset: LEAN_MS / (LEAN_MS + LEAN_BACK_MS),
            },
            { transform: squash(n, 1, 1) },
          ],
          { duration: (LEAN_MS + LEAN_BACK_MS) * k, easing: EASE_SETTLE },
        );
        await wait(LEAN_MS * k);
        return;
      }
      const n: [number, number] = [-c.normal[0], -c.normal[1]];
      await done(
        tilt.animate(
          [
            { transform: squash(n, 1, 1) },
            { transform: squash(n, 0.88, 1.12), offset: 90 / SETTLE_MS },
            { transform: squash(n, 1.03, 0.97), offset: 170 / SETTLE_MS },
            { transform: squash(n, 1, 1) },
          ],
          { duration: SETTLE_MS * k, easing: EASE_SETTLE },
        ),
      );
      // Визит сняли, пока капля оседала: тело не разворачивается.
      if (my !== gen || !host || !drop) return;
      // Разворот капли в тело: тело растёт из капли, капля растворяется в нём.
      splashAt = null;
      reaction = null;
      paint(c.size);
      const from = DROP / c.size;
      host.animate(
        [
          { transform: `scale(${from})`, opacity: 0 },
          { transform: 'scale(1)', opacity: 1 },
        ],
        { duration: UNFOLD_MS * k, easing: EASE_SETTLE, fill: 'forwards' },
      );
      await done(
        drop.animate([{ opacity: 1 }, { opacity: 0 }], { duration: UNFOLD_MS * k, fill: 'forwards' }),
      );
    },

    async react(g: ObserverGesture, c: Contact, finale?: CourierFinale | null) {
      if (dropOnly(c.kind) || !finale) {
        const el = drop;
        if (!el) return;
        el.removeAttribute('data-gesture');
        void getComputedStyle(el).getPropertyValue('animation-name');
        el.setAttribute('data-gesture', g);
        await wait(HOME_GESTURE_MS[g]);
        if (el.getAttribute('data-gesture') === g) el.removeAttribute('data-gesture');
        return;
      }
      const now = performance.now();
      if (finale === 'success') reaction = { kind: 'success', at: now };
      else splashAt = now;
      paint(shownSize || c.size);
      await wait(FINALE_MS[finale]);
    },

    async lift(c: Contact) {
      if (dropOnly(c.kind) || !host || !drop || shownSize === 0) return;
      const my = gen;
      const to = DROP / shownSize;
      host.animate(
        [
          { transform: 'scale(1)', opacity: 1 },
          { transform: `scale(${to})`, opacity: 0 },
        ],
        { duration: FOLD_MS * k, easing: 'ease-in', fill: 'forwards' },
      );
      await done(drop.animate([{ opacity: 0 }, { opacity: 1 }], { duration: FOLD_MS * k, fill: 'forwards' }));
      if (my !== gen || !host || !drop) return;
      paint(0);
      for (const a of host.getAnimations()) a.cancel();
      for (const a of drop.getAnimations()) a.cancel();
    },

    pace(next: number) {
      k = next;
    },

    unmount() {
      gen++;
      const r = root;
      root = null;
      // Корень React снимается вне рендера провайдера.
      if (r) window.setTimeout(() => r.unmount(), 0);
      host?.remove();
      drop?.remove();
      if (skin) delete skin.dataset.slot;
      tilt = skin = drop = host = null;
      shownSize = 0;
      splashAt = null;
      reaction = null;
    },
  };
}
